import { FilmStrip } from "@phosphor-icons/react";
import { useTranslation } from "@/contexts/LanguageContext";
import { FILM_CAMERAS } from "@/data/filmCameras";

const F_STOPS = [1.4, 2, 2.8, 4, 5.6, 8, 11, 16, 22];
const fmt = (s) => (s >= 1 ? `${Math.round(s * 10) / 10}s` : `1/${Math.round(1 / s)}`);
const fstop = (f) => `f/${f}`;
const stopIdx = (f) => F_STOPS.reduce((best, v, i) => (Math.abs(v - f) < Math.abs(F_STOPS[best] - f) ? i : best), 0);

// Clamp the suggested exposure to the body's top shutter speed by stopping down instead.
const planBracket = (shutter, aperture, maxShutter) => {
  const fastest = 1 / maxShutter;
  let base = shutter;
  let fi = stopIdx(aperture);
  let limited = false;
  while (base < fastest) {
    if (fi >= F_STOPS.length - 1) { base = fastest; limited = "film"; break; }
    base *= 2; fi += 1; limited = limited || "aperture";
  }
  const f = limited ? F_STOPS[fi] : aperture;
  const under = base / 2 >= fastest ? { s: base / 2, f } : { s: base, f: F_STOPS[Math.min(fi + 1, F_STOPS.length - 1)] };
  return { limited, base: { s: base, f }, under, over: { s: base * 2, f } };
};

export const FilmBracketTips = ({ results, filmId }) => {
  const { lang } = useTranslation();
  const es = lang === "es";
  const r = results.recommended;
  const film = FILM_CAMERAS.find((c) => c.id === filmId);
  const max = film?.maxShutter || 1000;
  const shown = String(r.shutter_speed).match(/^1\/(\d+)/);
  const plan = planBracket(shown ? 1 / Number(shown[1]) : r.shutter_speed_value, r.aperture_value, max);
  const frames = [
    { key: "under", stop: "−1", ...plan.under, label: es ? "Bajo" : "Under" },
    { key: "base", stop: "0", ...plan.base, label: es ? "Sugerida" : "Suggested" },
    { key: "over", stop: "+1", ...plan.over, label: es ? "Sobre" : "Over" },
  ];
  return (
    <div className="mt-6 bg-[#121214]/80 border border-[#F5A524]/30 rounded-xl p-5" data-testid="film-bracket-tips">
      <p className="text-[#F5A524] text-xs uppercase tracking-[0.2em] font-semibold mb-1 flex items-center gap-2">
        <FilmStrip weight="fill" className="w-4 h-4" />
        {es ? "Bracketing en película" : "Film bracketing"}
      </p>
      <p className="text-[#A1A1AA] text-sm mb-4">
        {es
          ? `Con tu ${film?.label} no puedes revisar la foto en pantalla, así que dispara 3 cuadros: el sugerido, uno un paso abajo y uno un paso arriba.`
          : `With your ${film?.label} you can't check the shot on a screen, so take 3 frames: the suggested one, one a stop under and one a stop over.`}
      </p>
      {plan.limited && (
        <p className="text-[#F5A524] text-xs mb-4" data-testid="film-bracket-limit-note">
          {plan.limited === "film"
            ? es
              ? `Tu cámara llega solo a 1/${max}. Aun en f/22 hay demasiada luz: usa película más lenta (ISO 50–100) o un filtro ND.`
              : `Your camera tops out at 1/${max}. Even at f/22 there's too much light: use slower film (ISO 50–100) or an ND filter.`
            : es
              ? `Tu cámara llega solo a 1/${max}, así que cerramos la apertura para la misma exposición.`
              : `Your camera tops out at 1/${max}, so the aperture is stopped down for the same exposure.`}
        </p>
      )}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {frames.map((f) => (
          <div
            key={f.key}
            className={`rounded-lg border p-2 sm:p-3 text-center ${f.key === "base" ? "border-[#F5A524]/50 bg-[#F5A524]/10" : "border-white/10 bg-[#0A0A0A]"}`}
            data-testid={`film-bracket-${f.key}`}
          >
            <p className="text-[#71717A] text-[10px] uppercase tracking-wider">{f.label}</p>
            <p className="text-[#52525B] text-[10px]">{f.stop} EV</p>
            <p className="text-white font-mono text-base sm:text-lg">{fmt(f.s)}</p>
            <p className="text-[#A1A1AA] font-mono text-xs">{fstop(f.f)}</p>
          </div>
        ))}
      </div>
      <p className="text-[#71717A] text-xs leading-relaxed">
        {es
          ? "Negativo color/B&N tolera bien la sobreexposición — si dudas, quédate con +1. En diapositiva (slide) haz el bracketing en medios pasos (±½), porque se quema fácil."
          : "Color and B&W negative film handle overexposure well — when in doubt, keep the +1 frame. With slide film, bracket in half stops (±½) because highlights blow out easily."}
      </p>
    </div>
  );
};
